import { CommunicationApi } from '@app/api';
import { ChangeAuthor } from '@app/components/ChangeAuthor';
import { DataLoadError } from '@app/components/DataLoadError';
import { Communication, CommunicationChange } from '@app/model/Communication';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { ddMMyyyyHHmmFormat } from '@app/utils/functions';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner
} from '@patternfly/react-core';
import { HistoryIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

export interface CommunicationHistoryModalProps {
  /** The communication whose history to show. Null closes the modal. */
  communication: Communication | null;
  onClose(): void;
}

/** ADD, MOD and DEL as an operator would describe them. */
const CHANGE_LABELS: Record<CommunicationChange['type'], { text: string; color: 'blue' | 'orange' | 'grey' }> = {
  ADD: { text: 'Overridden', color: 'blue' },
  MOD: { text: 'Changed', color: 'orange' },
  DEL: { text: 'Reset', color: 'grey' }
};

/** The columns of the override row, in the order they read in the editor. */
const COLUMNS: { field: keyof CommunicationChange; label: string }[] = [
  { field: 'enabled', label: 'Raised' },
  { field: 'push', label: 'Pushed' },
  { field: 'email', label: 'Emailed' },
  { field: 'subjectTemplate', label: 'Subject' },
  { field: 'pushMessageTemplate', label: 'Message' },
  { field: 'emailBodyTemplate', label: 'Email body' },
  { field: 'to', label: 'To' },
  { field: 'cc', label: 'Cc' },
  { field: 'bcc', label: 'Bcc' }
];

/**
 * Every recorded change to one communication. Read on open rather than with the list, because it
 * is one request per communication and an operator asks about one at a time.
 *
 * This matters more here than it does for a setting. **"Who turned off the mail that should have
 * gone out, and when" is the first question anybody will ask of this table**, which is why the
 * endpoint is admin-only and why what each revision held is spelled out rather than summarised.
 *
 * A row shows the override as it stood at that revision, not a diff, because that is what Envers
 * keeps. Fields it does not list were tracking the code at that point — which is a real state and
 * not a gap in the record.
 */
export const CommunicationHistoryModal: React.FunctionComponent<CommunicationHistoryModalProps> = ({
  communication,
  onClose
}) => {
  const [changes, setChanges] = React.useState<CommunicationChange[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<ApiError | null>(null);

  const key = communication?.name;

  const load = React.useCallback(async () => {
    if (!key) return;

    setIsLoading(true);
    setError(null);
    try {
      setChanges(await CommunicationApi.history(key));
    } catch (err) {
      setError(toApiError(err));
      setChanges([]);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <Modal
      variant={ModalVariant.large}
      isOpen={!!communication}
      onClose={onClose}
      aria-label="Communication change history"
    >
      <ModalHeader
        title={`History — ${communication?.name ?? ''}`}
        titleIconVariant={HistoryIcon}
        description="What the override held after each change. Anything not listed on a row was
                     tracking the code at that point."
      />
      <ModalBody>
        {isLoading && (
          <Bullseye>
            <Spinner aria-label="Loading history" />
          </Bullseye>
        )}

        {!isLoading && error && (
          <DataLoadError error={error} entityLabel="the change history" onRetry={() => void load()} />
        )}

        {!isLoading && !error && changes.length === 0 && (
          <Bullseye>
            <EmptyState headingLevel="h4" icon={HistoryIcon} titleText="Never changed">
              <EmptyStateBody>
                Nobody has changed this communication, so every part of it is still the wording,
                the channels and the recipients declared in the code.
              </EmptyStateBody>
            </EmptyState>
          </Bullseye>
        )}

        {!isLoading && !error && changes.length > 0 && (
          <Table aria-label="Communication change history" variant="compact">
            <Thead>
              <Tr>
                {/* Not narrower than this: at width 10 PatternFly ellipsises the header itself. */}
                <Th width={10}>Revision</Th>
                <Th width={15}>When</Th>
                <Th width={15}>Who</Th>
                <Th width={10}>Change</Th>
                <Th>What it held</Th>
              </Tr>
            </Thead>
            <Tbody>
              {changes.map(change => {
                const label = CHANGE_LABELS[change.type];
                return (
                  <Tr key={`${change.revision}-${change.type}`}>
                    <Td dataLabel="Revision">{change.revision}</Td>
                    <Td dataLabel="When">{ddMMyyyyHHmmFormat(change.changedAt)}</Td>
                    <Td dataLabel="Who"><ChangeAuthor username={change.username} /></Td>
                    <Td dataLabel="Change">
                      <Label isCompact color={label?.color ?? 'grey'}>{label?.text ?? change.type}</Label>
                    </Td>
                    <Td dataLabel="What it held" modifier="breakWord">
                      <ChangedFields change={change} />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

/**
 * The columns the row was answering for at one revision.
 *
 * Nothing at all is the honest answer for a reset: Envers keeps `store_data_at_delete` at false,
 * so a delete revision carries no values, and inventing "everything went back to the code" from
 * the absence of data would be a guess dressed as a record — even though it is what happened.
 */
const ChangedFields: React.FunctionComponent<{ change: CommunicationChange }> = ({ change }) => {
  const held = COLUMNS.filter(({ field }) => change[field] !== null && change[field] !== undefined);

  if (held.length === 0) {
    return (
      <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
        {change.type === 'DEL' ? 'the override was dropped' : 'nothing overridden'}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {held.map(({ field, label }) => (
        <div key={field}>
          <b>{label}:</b>{' '}
          <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>
            {String(change[field])}
          </span>
        </div>
      ))}
    </div>
  );
};
